import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AlertController, IonicPage, NavController, NavParams } from 'ionic-angular';
import { filter, get, includes, isEmpty, keys, sortBy, toArray } from 'lodash';
import * as moment from 'moment';
import 'moment/locale/nl';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/withLatestFrom';
import { IUserWeeklyTimesheet } from '../../../typings/api';
import { IActivity, ICorrection, IUser } from '../../../typings/timetracker/timetracker';
import { UserSelectorComponent } from '../../components/user-selector/user-selector';
import { WeekSelectorComponent } from '../../components/week-selector/week-selector';
import { EmployeeAutoCompleteService } from '../../providers/employee-auto-complete';
import { UserService } from '../../providers/user.service';
import { TimesheetService } from './providers/timesheet.service';
interface ITimesheetRow {
  days: {
    '1': ICorrection;
    '2': ICorrection;
    '3': ICorrection;
    '4': ICorrection;
    '5': ICorrection;
    '6': ICorrection;
    '7': ICorrection;
  };
  total: number;
  activity: {
    label: string;
    id: number;
    type: string;
  };
};

/**
 * Generated class for the TimesheetsPage page.
 *
 * See https://ionicframework.com/docs/components/#navigation for more info on
 * Ionic pages and navigation.
 */

@IonicPage()
@Component({
  selector: 'page-timesheets',
  templateUrl: 'timesheets.html',
  encapsulation: ViewEncapsulation.None,
  providers: [ EmployeeAutoCompleteService ]
})
export class TimesheetsPage implements OnInit {

  /** Determines whether the timesheet has been approved or not */
  public get timeSheetApproved(): boolean {
    return get(this.timesheetData, 'timesheet.approved', false);
  }

  currentWeekNumber: number
  currentYear: number;

  public timesheetRows: ITimesheetRow[];
  public timesheet$: Observable<IUserWeeklyTimesheet>;
  public timesheetData: IUserWeeklyTimesheet;
  public activities$: Observable<IActivity[]>;
  public missingActivities: IActivity[] = [];
  public daysOfTimesheet: Date[] = [];
  public lastFocusElementID:string;

  @ViewChild( 'userSelector' ) userSelector: UserSelectorComponent;
  @ViewChild( 'weekSelector' ) weekSelector: WeekSelectorComponent;

  /**
   * @param {ITimesheetRow} row The row where the correction is made
   * @param {number} index The day where the correction is made
   * @param {Event} $event The event associated with the input change
   */
  public addCorrection( row: ITimesheetRow, day: number, $event: Event ) {
    // Add save logic here
    //
    ( $event.target as any ).classList.add( 'correction' )
  }

  /** Approves a timesheet */
  public approveTimesheet() {
    this.$alertService.create( {
      title: 'Timeheet goedkeuren',
      message: 'Weet je zeker dat je deze timesheet wilt goedkeuren?',
      buttons: [
        {
          text: 'Annuleren',
          role: 'cancel'
        },
        {
          text: 'Akkorderen',
          handler: () => {
            this.$timesheetService.dispatchApproveTimesheet( this.weekSelector.currentWeekNumber, this.weekSelector.currentYear, this.userSelector.selectedUser.id, true );
          }
        }
      ]
    } ).present();
  }

  /** Unlocks a timesheet */
  public unlockTimeSheet() {
    this.$alertService.create({
      title: 'Timesheet ontgrendelen',
      message: 'Weet je zeker dat je deze timesheet weer wilt ontgrendelen?',
      buttons: [
        {
          text: 'Annuleren',
          role: 'cancel'
        },
        {
          text: 'Ontgrendelen',
          handler: () => {
            this.$timesheetService.dispatchApproveTimesheet( this.weekSelector.currentWeekNumber, this.weekSelector.currentYear, this.userSelector.selectedUser.id, false );
          }
        }
      ]
    }).present();
  }

  public rememberFocus( $event ) {
    const elementID = get( $event, 'target.id' );
    if ( this.lastFocusElementID !== elementID ) {
      this.lastFocusElementID = elementID;
    }
  }

  public selectUser( user: IUser ) {
    if ( user ) {
      console.log( '[TIMESHEET] Change user', user );
      this.$timesheetService.setSelectedUser( user );
      this.$timesheetService.dispatchSelectTimesheetFetch( user.id, this.currentWeekNumber, this.currentYear );
    }
    this.blurLastElement();
  }

  /**
   * @param {number} userId The user ID to retrieve the details for
   */
  public getUserDetails( userId: number ): Observable<IUser> {
    return this.$userService.selectUserByIdFetch( userId );
  }

  public selectDate( date: moment.Moment ) {
    console.log( '[TIMESHEET] Change date', date );

    const thisMoment        = moment( date );
    this.currentWeekNumber  = thisMoment.isoWeek();
    this.currentYear        = thisMoment.isoWeekYear();

    this.$timesheetService.dispatchSelectTimesheetFetch( this.userSelector.selectedUser.id, this.currentWeekNumber, this.currentYear );
  }

  public maskInput( $event: KeyboardEvent ) {
    const pattern = /\d+(\.\d{1,2})?/;
    const value = (<HTMLInputElement>$event.target).value;
    const keyCode = $event.keyCode;

    // Check if the pattern is ok. But we do allow backspaces
    //
    if ( $event && value && keyCode !== 8 && !pattern.test( value ) ) {
      $event.preventDefault();
      $event.stopPropagation();
    } else if ( value && parseInt( value, 10 ) > 24 ) {
      (<HTMLInputElement>$event.target).value = '24';
    }
  }

  public getActivityById( activityId: number ): IActivity {
    let result: IActivity;
    this.$timesheetService.availableActivities$
    .take( 1 )
    .subscribe( ( activities ) => {
      result = activities.find( ( activity ) => activity.id === activityId );
    } );
    return result;
  }

  /**
   * Update / add or delete a correction
   *
   * @param {ITimesheetRow} correction
   * @param {number} weekDay
   * @param {Event} $event
   * @memberof TimesheetsPage
   */
  public updateCorrection( correction: ITimesheetRow, weekDay: number, $event: Event ) {
    console.log( '[UPDATE-CORRECTION]', correction );
    let duration = get( $event, 'target.value' );

    // No duration provided? Delete the correction otherwise create / update it
    //
    if ( isEmpty( duration ) ) {
      ( $event.target as any ).classList.remove( 'correction' );
      this.$timesheetService.dispatchDeleteCorrection( correction.activity.id, this.userSelector.selectedUser.id, weekDay, this.weekSelector.currentWeekNumber, this.weekSelector.currentYear );
    } else {
      // Durations are in seconds so we need to convert it back from hours back to seconds
      //
      ( $event.target as any ).classList.add( 'correction' );
      this.$timesheetService.dispatchUpdateCorrection( correction.activity.id, this.userSelector.selectedUser.id, weekDay, this.weekSelector.currentWeekNumber, this.weekSelector.currentYear, duration );
    }
  }

  /**
   * Create a UI format activity from an IActivity
   * @param {IActivity} activity the activity to tranform
   */
  private createEmptyActivity( activity: IActivity ) {
    return {
      days: {
        '1': undefined,
        '2': undefined,
        '3': undefined,
        '4': undefined,
        '5': undefined,
        '6': undefined,
        '7': undefined,
      },
      total: 0,
      activity: {
        id: activity.id,
        label: activity ? activity.name : 'Onbekend',
        type: activity ? activity.activity_type : 'Onbekend'
      }
    };
  }

  /**
   * Controller constructor
   *
   * @param {NavController} navCtrl
   * @param {NavParams} navParams
   * @param {EmployeeAutoCompleteService} employeeAutoCompleteService
   * @param {UserService} $userService
   * @param {TimesheetService} $timesheetService
   */
  constructor(
      public navCtrl: NavController,
      public navParams: NavParams,
      private $timesheetService: TimesheetService,
      private $userService: UserService,
      private $alertService: AlertController
  ) { }


  ngOnInit() {

    const localStorageData = this.$timesheetService.getStoredTimesheetData();

    // Determine selected week and year
    //
    if ( localStorageData.isoWeek && localStorageData.year )
    {
      this.currentWeekNumber = localStorageData.isoWeek;
      this.currentYear = localStorageData.year;
    } else
    {
      const now = moment();
      this.currentWeekNumber = now.isoWeek();
      this.currentYear = now.isoWeekYear();
    }

    // Update week selector
    //
    this.weekSelector.setWeek( this.currentWeekNumber, this.currentYear );

    this.activities$ = this.$timesheetService.availableActivities$.asObservable();

    this.$timesheetService.getSelectTimesheetFetch()
    .filter( value => !isEmpty( value ) )
    .withLatestFrom( this.activities$ )
    .subscribe( ( [ timesheet, activities ] )=> {

      this.timesheetData = timesheet;

      console.log( '[TIMESHEET] selected timesheet', timesheet );

      // Ceate timesheet days
      //
      this.daysOfTimesheet.length = 0;

      let isoWeek = this.weekSelector.setWeek( timesheet.iso_week, timesheet.year );

      for ( let i = 0; i<= 6; i++ ) {
        this.daysOfTimesheet.push( isoWeek.toDate() );
        isoWeek = isoWeek.add( 1, 'day' );
      }

      // Create timesheet rows
      //
      const week: { [ key: number ]: ITimesheetRow } = {};

      for ( let row of timesheet.rows ) {
        const activity = this.getActivityById( row.activityId );
        const activityRow: ITimesheetRow = week[ row.activityId ] ? week[ row.activityId ] : this.createEmptyActivity( activity );

        activityRow.days[ `${row.weekday}` ] = row;
        activityRow.total += row.duration;
        if ( activity ) {
          week[ activity.id ] = activityRow;
        }

        // Round the duration to two decimals after the comma
        //
        if ( Number( row.duration ) === row.duration && row.duration % 1 !== 0 ) {
          row.duration = Math.round( ( row.duration * 100 ) ) / 100;
        }
      }


      const currentActivityIds = keys( week );
      const missingActivities = filter( activities, ( activity ) => !includes( currentActivityIds, `${activity.id}` ) );

      for ( let missingActivity of missingActivities ) {
        week[ missingActivity.id ] = this.createEmptyActivity( missingActivity );
      }

      // Create array from timesheet and sort by id
      //
      this.timesheetRows = sortBy( toArray( week ), 'id' ).sort( ( ( value ) => {
        if ( value.activity.type === 'labor' ) {
          return -1
        } else {
          return 1;
        }
      } ));
      this.focusLastElement();
    } );
  }

  // Put focus on the last known selected element
  //
  focusLastElement() {
    if ( this.lastFocusElementID ) {
      setTimeout( () => {
        const element: HTMLInputElement = <HTMLInputElement>document.getElementById( this.lastFocusElementID );
        if ( element ) {
          element.focus();
          element.setSelectionRange(0, element.value.length );
        }
      }, 0 );
    }
  }

  // Blur last known selected element
  //
  blurLastElement() {
    const elementId = this.lastFocusElementID;
    delete this.lastFocusElementID;
    if ( elementId ) {
      setTimeout( () => {
        const element = document.getElementById( elementId );
        if ( element ) {
          element.blur();
        }
      }, 0 );
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/timesheets/timesheets.ts